import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Row,
  Col,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import LedgersService from "../../../services/AccountingServices/LedgerService";
import LedgerTypeService from "../../../services/AccountingServices/LedgerTypeService";

const LedgerForm = ({ isOpen, toggle, selectedItem, refetch }) => {
  const [loading, setLoading] = useState(false);
  const [ledgerTypes, setLedgerTypes] = useState([]);
  const [parentLedgers, setParentLedgers] = useState([]);

  useEffect(() => {
    if (isOpen) {
      fetchDropdownData();
    }
  }, [isOpen]);

  const fetchDropdownData = async () => {
    try {
      const [types, ledgers] = await Promise.all([
        LedgerTypeService.getList(),
        LedgersService.getList(),
      ]);
      setLedgerTypes(Array.isArray(types) ? types : types?.items || []);
      
      const allLedgers = Array.isArray(ledgers) ? ledgers : ledgers?.items || [];
      // Filter out current ledger to prevent circular parent reference
      setParentLedgers(allLedgers.filter((l) => l.id !== selectedItem?.id));
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    code: Yup.string().required("Code is required"),
    ledgerTypeId: Yup.number().required("Ledger Type is required").min(1, "Please select a type"),
    description: Yup.string(),
    parentLedgerId: Yup.number().nullable(),
  });

  const formik = useFormik({
    initialValues: {
      id: selectedItem?.id || 0,
      name: selectedItem?.name || "",
      code: selectedItem?.code || "",
      ledgerTypeId: selectedItem?.ledgerTypeId || "",
      description: selectedItem?.description || "",
      parentLedgerId: selectedItem?.parentLedgerId || "",
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const payload = {
          ...values,
          parentLedgerId: values.parentLedgerId || null,
        };
        
        if (values.id) {
          await LedgersService.update(values.id, payload);
          toast.success("Ledger updated successfully");
        } else {
          await LedgersService.create(payload);
          toast.success("Ledger created successfully");
        }
        refetch();
        toggle();
      } catch (error) {
        console.error("Submission error:", error);
        toast.error(error.response?.data?.message || "Failed to save ledger");
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle} className="border-bottom-0">
        {selectedItem ? "Edit Ledger" : "Add Ledger"}
      </ModalHeader>
      <form onSubmit={formik.handleSubmit}>
        <ModalBody className="py-4">
          <Row>
            <Col lg={6}>
              <FormGroup>
                <Label for="name" className="fw-semibold small text-uppercase text-muted">
                  Ledger Name <span className="text-danger">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="e.g. Loan, Salary, Bank Account"
                  className="form-control-lg border-2"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.name && !!formik.errors.name}
                />
                <FormFeedback>{formik.errors.name}</FormFeedback>
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="code" className="fw-semibold small text-uppercase text-muted">
                  Ledger Code <span className="text-danger">*</span>
                </Label>
                <Input
                  id="code"
                  name="code"
                  type="text"
                  placeholder="e.g. 1001"
                  className="form-control-lg border-2"
                  value={formik.values.code}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.code && !!formik.errors.code}
                />
                <FormFeedback>{formik.errors.code}</FormFeedback>
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="ledgerTypeId" className="fw-semibold small text-uppercase text-muted">
                  Ledger Type <span className="text-danger">*</span>
                </Label>
                <Input
                  id="ledgerTypeId"
                  name="ledgerTypeId"
                  type="select"
                  className="form-select-lg border-2"
                  value={formik.values.ledgerTypeId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.ledgerTypeId && !!formik.errors.ledgerTypeId}
                >
                  <option value="">Select Ledger Type</option>
                  {ledgerTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name} ({type.category})
                    </option>
                  ))}
                </Input>
                <FormFeedback>{formik.errors.ledgerTypeId}</FormFeedback>
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup>
                <Label for="parentLedgerId" className="fw-semibold small text-uppercase text-muted">
                  Parent Ledger (Optional)
                </Label>
                <Input
                  id="parentLedgerId"
                  name="parentLedgerId"
                  type="select"
                  className="form-select-lg border-2"
                  value={formik.values.parentLedgerId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value="">No Parent</option>
                  {parentLedgers.map((ledger) => (
                    <option key={ledger.id} value={ledger.id}>
                      {ledger.name} ({ledger.code})
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col lg={12}>
              <FormGroup className="mb-0">
                <Label for="description" className="fw-semibold small text-uppercase text-muted">
                  Description
                </Label>
                <Input
                  id="description"
                  name="description"
                  type="textarea"
                  rows={3}
                  placeholder="Details about this ledger"
                  className="form-control border-2"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter className="border-top-0 d-flex gap-2">
          <Button color="light" size="lg" className="px-4" onClick={toggle} disabled={loading}>
            Cancel
          </Button>
          <Button color="primary" size="lg" className="px-4 shadow-sm" type="submit" disabled={loading}>
            {loading ? "Saving..." : selectedItem ? "Update Ledger" : "Create Ledger"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default LedgerForm;
